import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Btn, DangerBtn, GhostBtn, Input } from './AnalyticsAdmin.styles';

const pop = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: none; }
`;

const Dialog = styled.dialog`
  width: min(28rem, calc(100vw - 2rem));
  margin: auto; /* global "* { margin: 0 }" would pin the modal to the top-left corner */
  padding: 0;
  border: 1px solid var(--border-accent);
  border-radius: 0.875rem;
  background: var(--card);
  color: var(--text-primary);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
  will-change: transform, opacity;
  animation: ${pop} 160ms ease-out;

  &::backdrop {
    /* Solid scrim; a backdrop blur stutters while the dialog animates in. */
    background: rgba(4, 5, 6, 0.78);
  }

  form {
    padding: 1.5rem;
  }

  h2 {
    font-size: 1.25rem;
    letter-spacing: -0.01em;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.875rem;
    white-space: pre-line;
  }

  label {
    display: block;
    margin-top: 1.125rem;
    font-size: 0.8125rem;
    color: var(--text-secondary);
  }

  label code {
    font-family: var(--font-mono);
    color: var(--text-primary);
    background: var(--surface-3);
    padding: 0.0625rem 0.375rem;
    border-radius: 0.25rem;
  }

  input {
    width: 100%;
    margin-top: 0.5rem;
  }
`;

const Icon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  background: ${(p) => (p.$danger ? 'rgba(240, 82, 82, 0.12)' : 'rgba(255, 122, 46, 0.12)')};
  color: ${(p) => (p.$danger ? '#f07a7a' : 'var(--brand)')};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

function DialogView({ request, onClose }) {
  const ref = useRef(null);
  const [value, setValue] = useState(request.defaultValue || '');
  const isPrompt = request.kind === 'prompt';
  const needsText = Boolean(request.requireText);

  useEffect(() => {
    const node = ref.current;
    if (node && !node.open) node.showModal();
  }, []);

  const canConfirm = isPrompt
    ? value.trim().length > 0 && value.trim() !== (request.defaultValue || '').trim()
    : !needsText || value.trim() === request.requireText;

  const submit = (e) => {
    e.preventDefault();
    if (!canConfirm) return;
    onClose(isPrompt ? value.trim() : true);
  };

  const ConfirmButton = request.danger ? DangerBtn : Btn;

  return (
    <Dialog
      ref={ref}
      aria-labelledby="confirm-dialog-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose(isPrompt ? null : false);
      }}
      onClick={(e) => {
        // Click on the backdrop (the dialog element itself, outside the form) cancels.
        if (e.target === ref.current) onClose(isPrompt ? null : false);
      }}
    >
      <form onSubmit={submit}>
        <Icon $danger={request.danger} aria-hidden="true">
          {request.danger ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            </svg>
          ) : isPrompt ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
          )}
        </Icon>
        <h2 id="confirm-dialog-title">{request.title}</h2>
        {request.message && <p>{request.message}</p>}

        {isPrompt && (
          <label>
            {request.label}
            <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)} onFocus={(e) => e.target.select()} />
          </label>
        )}

        {needsText && (
          <label>
            Type <code>{request.requireText}</code> to confirm
            <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)} autoComplete="off" spellCheck={false} />
          </label>
        )}

        <Actions>
          <GhostBtn type="button" onClick={() => onClose(isPrompt ? null : false)} autoFocus={!isPrompt && !needsText}>
            Cancel
          </GhostBtn>
          <ConfirmButton type="submit" disabled={!canConfirm} $solid={request.danger}>
            {request.confirmLabel || (isPrompt ? 'Save' : 'Confirm')}
          </ConfirmButton>
        </Actions>
      </form>
    </Dialog>
  );
}

/**
 * Promise-based replacement for window.confirm / window.prompt.
 *   const [dialog, ask] = useConfirmDialog();
 *   if (!(await ask.confirm({ title, message, danger, requireText }))) return;
 *   const name = await ask.prompt({ title, label, defaultValue }); // string | null
 * Render {dialog} once in the component tree.
 */
export default function useConfirmDialog() {
  const [request, setRequest] = useState(null);

  const open = useCallback((kind, options) => new Promise((resolve) => setRequest({ ...options, kind, resolve })), []);
  const ask = useMemo(() => ({
    confirm: (options) => open('confirm', options),
    prompt: (options) => open('prompt', options),
  }), [open]);

  const close = useCallback((result) => {
    setRequest((current) => {
      current?.resolve(result);
      return null;
    });
  }, []);

  const dialog = request ? <DialogView key={request.title} request={request} onClose={close} /> : null;
  return [dialog, ask];
}
